import * as React from "react";
import { View, Text, StyleSheet, Animated, Easing, StyleProp, ViewProps } from "react-native";

const defaultRenderLoading = ({ children }: React.PropsWithChildren<{}>) => <>{children}</>;

export type LoadingProps = React.PropsWithChildren<{
  loading?: boolean;
  renderLoading?: (props: React.PropsWithChildren<{}>) => JSX.Element;
  style?: StyleProp<ViewProps>;
}>;

function Loading({
  loading = false,
  children,
  renderLoading: RenderLoading = defaultRenderLoading,
  style,
}: LoadingProps) {
  const { current: rotateAnim } = React.useRef(new Animated.Value(0)); // Initial value for opacity: 0

  React.useEffect(() => {
    const timing = Animated.timing(rotateAnim, {
      toValue: 1,
      duration: 2000,
      easing: Easing.bounce,
      useNativeDriver: false,
    });

    const restart = () => {
      timing.reset();
      timing.start(restart);
    };

    timing.start(restart);

    return () => {
      timing.stop();
    };
  }, [rotateAnim]);

  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  return (
    <View style={[styles.container, style]}>
      {children}
      {loading && (
        <RenderLoading>
          <Animated.View
            style={{
              transform: [{ rotate }, { perspective: 1000 }],
            }}
          >
            <Text>⌛</Text>
          </Animated.View>
        </RenderLoading>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    textAlign: "center",
  },
});

export default Loading;
